import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import { fmtNum, formatDate, ORDER_STATUSES, STATUS_COLORS } from "@/lib/constants";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [companyF, setCompanyF] = useState("all");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (statusF !== "all") params.status = statusF;
    if (companyF !== "all") params.company_id = companyF;
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data);
  };

  useEffect(() => {
    api.get("/admin/companies").then(({ data }) => setCompanies(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, statusF, companyF]);

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-tag">Siparişler</span>
          <h1 className="font-serif text-4xl mt-2">Sipariş Yönetimi</h1>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/export-orders.csv`}
          className="btn-luxe-outline text-xs"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            // axios doesn't help for downloads; need bearer. Use fetch instead via JS.
            e.preventDefault();
            const token = localStorage.getItem("evser_token");
            fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.blob())
              .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "siparisler.csv";
                a.click();
                URL.revokeObjectURL(url);
              });
          }}
        >
          CSV Dışa Aktar
        </a>
      </div>

      <div className="bg-white border border-[#eae5dc] p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655c]" />
          <Input placeholder="Sipariş No" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Durum"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={companyF} onValueChange={setCompanyF}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Firma"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Firmalar</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#eae5dc] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-[#f7f5f0] text-xs uppercase tracking-wider text-[#6b655c]">
              <th className="text-left py-3 px-4">Sipariş No</th>
              <th className="text-left py-3 px-4">Firma</th>
              <th className="text-left py-3 px-4">Tarih</th>
              <th className="text-center py-3 px-4">Satır</th>
              <th className="text-right py-3 px-4">CM</th>
              <th className="text-right py-3 px-4">m²</th>
              <th className="text-center py-3 px-4">Durum</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-[#6b655c]">Sipariş yok</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[#f0ebe2]">
                <td className="py-3 px-4">
                  <Link to={`/admin/siparisler/${o.id}`} className="font-medium hover:text-[#c8a97e]" data-testid={`order-link-${o.order_no}`}>{o.order_no}</Link>
                </td>
                <td className="py-3 px-4">{o.company_name}</td>
                <td className="py-3 px-4 text-[#6b655c]">{formatDate(o.created_at)}</td>
                <td className="py-3 px-4 text-center">{(o.items || []).length}</td>
                <td className="py-3 px-4 text-right">{fmtNum((o.total_meters || 0) * 100)}</td>
                <td className="py-3 px-4 text-right font-medium">{fmtNum(o.total_sqm)}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-1 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
